import type { DrawingDocument } from '../model';
import type { EditorSelection } from './editor';
import { documentToAtoms, documentToLines } from './drawingDocumentProjections';
import { canvasRings } from './objectEditing';

export type CycleFill = NonNullable<DrawingDocument['depiction']['cycleFills']>[number];
export const cycleKey = (ids: readonly string[]) => JSON.stringify([...ids].sort());

/** An ordered closed path; opening a ring removes its fill, even when its atoms survive. */
export function isClosedCycle(
  atomIds: readonly string[],
  edges: DrawingDocument['structure']['graph']['edges']
): boolean {
  return (
    atomIds.length >= 3 &&
    new Set(atomIds).size === atomIds.length &&
    atomIds.every((id, i) => {
      const next = atomIds[(i + 1) % atomIds.length];
      return edges.some((e) => (e.source === id && e.target === next) || (e.target === id && e.source === next));
    })
  );
}

/** Reuse the editor's selectable small rings, including individual rings in fused systems. */
export function selectedCycles(document: DrawingDocument, selection: EditorSelection) {
  if (selection.atomIds.length < 3 && selection.bondIds.length < 3) return [];
  const selected = new Set(selection.atomIds);
  const bonds = new Set(selection.bondIds);
  const endpoints = Object.fromEntries(
    document.structure.graph.edges.map((edge) => [edge.id, [edge.source, edge.target] as const])
  );
  return canvasRings(documentToAtoms(document), documentToLines(document), endpoints).filter(
    (ring) => ring.atomIds.every((id) => selected.has(id)) || ring.bondIds.every((id) => bonds.has(id))
  );
}

export function styleCycleFills(
  document: DrawingDocument,
  selection: EditorSelection,
  color: string | undefined
): DrawingDocument {
  const rings = selectedCycles(document, selection);
  if (!rings.length) return document;
  const keys = new Set(rings.map((r) => cycleKey(r.atomIds)));
  const fills = (document.depiction.cycleFills ?? []).filter((fill) => !keys.has(cycleKey(fill.atomIds)));
  if (color !== undefined)
    fills.push(...rings.map((ring) => ({ atomIds: ring.atomIds as [string, string, string, ...string[]], color })));
  const { cycleFills: _old, ...depiction } = document.depiction;
  return { ...document, depiction: { ...depiction, ...(fills.length ? { cycleFills: fills } : {}) } };
}
